import { phoneProblem } from "@freightsimple/contact-rules/phoneProblem";
import type { PhoneProblem } from "@freightsimple/contact-rules/phoneProblem";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { isSimilar } from "@/Helpers/isSimilar";

const EMAIL_REGEX = new RegExp(
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
);
const NAME_REGEX = new RegExp(
  /^[a-zA-ZÀ-ÿ]+(([',. -][a-zA-ZÀ-ÿ -])?[a-zA-ZÀ-ÿ]*)*$/,
);

// The rule lives in @freightsimple/contact-rules so the Shopify embedded app applies the same one
// (ENG-6448). This function owns only the wording, which is per-surface on purpose.
const PHONE_MESSAGES: Record<PhoneProblem, ErrorMessageType> = {
  required: "Required",
  tooShort: "Phone number should be 10 digits",
  leadingOne: "Phone number should not start with 1",
  implausible: "Please enter a valid phone number",
};

export function errorMessageForPhoneNumber(
  phoneNumber: string | undefined,
): ErrorMessageType {
  const problem = phoneProblem(phoneNumber);
  return problem === undefined ? undefined : PHONE_MESSAGES[problem];
}

export function errorMessageForContactName(
  contactName: string | undefined,
): ErrorMessageType {
  if (contactName === undefined || contactName === "") {
    return "Required";
  }

  if (contactName.length === 1) {
    return "Too short";
  }

  if (!NAME_REGEX.test(contactName.trim())) {
    return "This does not seem to be a valid name";
  }

  return undefined;
}

export function errorMessageForEmail(
  emailAddress: string | undefined,
): ErrorMessageType {
  if (emailAddress === undefined || emailAddress === "") {
    return "Required";
  }

  if (emailAddress.length < 4) {
    return "Too short";
  }

  if (!EMAIL_REGEX.test(emailAddress)) {
    return "Please enter a valid email address";
  }

  return undefined;
}

function errorMessageForNotRequiredEmail(
  emailAddress: string | undefined,
): ErrorMessageType {
  if (emailAddress === undefined || emailAddress === "") {
    return;
  }
  return errorMessageForEmail(emailAddress);
}

export interface ContactErrors {
  contactName: ErrorMessageType;
  emailAddress: ErrorMessageType;
  phoneNumber: ErrorMessageType;
}

export function warningMessagesForContact(
  contact: AddContactContactType,
  location: AddLocationLocationType,
  context: LocationContext,
): ContactErrors {
  function contactNameWarning(): ErrorMessageType {
    if (
      contact.contactName !== undefined &&
      contact.contactName === location.businessName
    ) {
      if (location.locationType !== LocationType.Residential) {
        return `Is this a residential ${context}? If so please include in the quote for accurate pricing`;
      }
    }

    return undefined;
  }

  function emailAddressWarning(): ErrorMessageType {
    if (
      contact.emailAddress !== undefined &&
      contact.emailAddress.includes("csc-scc.gc.ca") &&
      location.locationType !== LocationType.Prison
    ) {
      return `Is this a prison ${context}? If so please include in the quote for accurate pricing`;
    }
    return undefined;
  }

  return {
    contactName: contactNameWarning(),
    phoneNumber: undefined,
    emailAddress: emailAddressWarning(),
  };
}

export const EmailRequired = {
  EmailRequired: 0,
  EmailNotRequired: 1,
} as const;
export type EmailRequired = (typeof EmailRequired)[keyof typeof EmailRequired];

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function contactsErrorMessagesForContact(
  contact: AddContactContactType,
  emailRequired: EmailRequired,
  comparisonContacts?: Map<string, AddContactContactType>,
): ContactErrors {
  const errors = {
    phoneNumber: errorMessageForPhoneNumber(contact.phoneNumber),
    contactName: errorMessageForContactName(contact.contactName),
    emailAddress:
      emailRequired === EmailRequired.EmailRequired
        ? errorMessageForEmail(contact.emailAddress)
        : errorMessageForNotRequiredEmail(contact.emailAddress),
  };

  // Check for duplicate email addresses with pickup and delivery
  if (errors.emailAddress === undefined && contact.emailAddress) {
    const pickupContact = comparisonContacts?.get("pickup");
    const deliveryContact = comparisonContacts?.get("delivery");

    if (
      pickupContact?.emailAddress &&
      isSimilar(contact.emailAddress, pickupContact.emailAddress)
    ) {
      errors.emailAddress = "Should not match pickup details";
    }
    if (
      deliveryContact?.emailAddress &&
      isSimilar(contact.emailAddress, deliveryContact.emailAddress)
    ) {
      errors.emailAddress = "Should not match delivery details";
    }
  }

  // Check for duplicate phone numbers with pickup and delivery
  if (errors.phoneNumber === undefined && contact.phoneNumber) {
    const pickupContact = comparisonContacts?.get("pickup");
    const deliveryContact = comparisonContacts?.get("delivery");

    if (
      pickupContact?.phoneNumber &&
      isSimilar(contact.phoneNumber, pickupContact.phoneNumber)
    ) {
      errors.phoneNumber = "Should not match pickup details";
    }
    if (
      deliveryContact?.phoneNumber &&
      isSimilar(contact.phoneNumber, deliveryContact.phoneNumber)
    ) {
      errors.phoneNumber = "Should not match delivery details";
    }
  }

  // Existing comparison logic
  if (errors.phoneNumber === undefined) {
    comparisonContacts?.forEach(function (value, key) {
      if (
        value.phoneNumber !== undefined &&
        contact.phoneNumber !== undefined
      ) {
        if (
          value.phoneNumber === contact.phoneNumber &&
          value.phoneNumberExtension === contact.phoneNumberExtension
        ) {
          errors.phoneNumber = `Same as ${key}`;
        }
      }
    });
  }
  return errors;
}
